import { AxiosError } from "axios";
import { useCallback, useEffect, useState } from "react";

import { useApiOperation } from "src/api/hooks";
import { providerDetailAsync } from "src/api/provider/provider-api";
import { BaseButton, BaseRadio } from "src/components";
import { useErrorModal } from "src/recoil/errorModal/hook";
import { useModal } from "src/recoil/modalState/hook";
import { ProductTypes } from "../../product-types";

import { changeProductAvailable_RPP24, getProductCheck_RCA94 } from "src/api/contract/contract-api";
import { ProviderPayletterModel, ProviderPopbillModel } from "src/api/provider/provider-types";
import useGetProduct from "src/hooks/product/useGetProduct";
import useUpdateProduct from "src/hooks/product/useUpdateProduct";
import { MakeContractAvailFieldLabels } from "src/pages/commonPopup/SearchSpaceProductPopup";
import { contractAvailFieldLabelMap } from "../../forms/basicInfoForm/libs";

/**
 * 계약 가능 여부 모달
 * 문서 https://pnpt.atlassian.net/wiki/spaces/dsplan/pages/865140986/Ctrl.room+-#03-Ctrl.room-%3E-%EC%83%81%ED%92%88-%3E-%EA%B8%B0%EB%B3%B8%EC%A0%95%EB%B3%B4(tab)---Read
 * @param productId 공간상품 ID
 * @param isContractAvailDefault 계약 가능 여부 기본값
 * @returns
 */
const ContractAvailModal = ({ productId, isContractAvailDefault }: { productId: string; isContractAvailDefault: boolean }) => {
  // 계약 가능 여부
  const [isContractAvail, setIsContractAvail] = useState(isContractAvailDefault);

  //프로바이더 정산정보 유효성 체크를 위한 프로바이더 상세 정보
  const [providerNormalCardApi, setProviderNormalCardApi] = useState<ProviderPayletterModel>();
  const [providerPopbillApi, setProviderPopbillApi] = useState<ProviderPopbillModel>();

  const { setBaseModal } = useModal();
  const { openErrorModal } = useErrorModal();
  // 공간상품 상세 get Api
  const { data: product, refetch } = useGetProduct({ productId: productId });

  const { mutateAsync: updateProduct } = useUpdateProduct();

  const { executeAsync: getProviderDetail } = useApiOperation(providerDetailAsync);

  /**
   * RCA94. 계약 상품 조회
   */
  const { executeAsync: getProductCheckRca94 } = useApiOperation(getProductCheck_RCA94, { noErrorModal: true });

  /**
   * RPP24. 계약 상품 사용 가능 여부 변경
   */
  const { executeAsync: changeProductAvailable } = useApiOperation(changeProductAvailable_RPP24, { noErrorModal: true });

  const fetchProviderDetail = useCallback(
    async (providerId: string) => {
      const providerDetail = await getProviderDetail({ providerId: providerId });

      if (providerDetail && providerDetail.data && providerDetail.data.data && providerDetail.data.data.payletterList) {
        setProviderNormalCardApi(providerDetail.data.data.payletterList.find((item: ProviderPayletterModel) => item.clientType === "NORMAL"));
        setProviderPopbillApi(providerDetail.data.data.provider);
      }
    },
    [getProviderDetail],
  );

  // 프로바이더 정산정보 유효성 체크를 위한 프로바이더 상세 정보 조회
  useEffect(() => {
    if (product?.providerId) {
      fetchProviderDetail(String(product.providerId));
    }
  }, [fetchProviderDetail, product?.providerId]);

  const openCustomErrorModel = (response: any) => {
    let errorList: string[] = [];

    if (response?.data?.meta?.errorCode === "eCT215") {
      try {
        // 1. 에러 데이터 파싱
        const origin = JSON.parse(response?.data?.meta?.errorData);

        // 2. 문자열에서 배열 추출 및 정제
        const missingProductAttrKeyList = origin?.missingProductAttrKeyList
          ?.replace(/[\[\]"]/g, "") // [], " 제거
          ?.split(",") // 콤마로 분리
          ?.map((key: string) => key.trim()); // 공백 제거

        // 3. 매핑되는 label만 필터링하여 반환
        missingProductAttrKeyList
          ?.map((key: string) => contractAvailFieldLabelMap.find((label) => label.name === key))
          ?.filter((label: MakeContractAvailFieldLabels): label is MakeContractAvailFieldLabels => label !== undefined)
          ?.forEach((label: MakeContractAvailFieldLabels) => errorList.push(label.label));
      } catch (error) {
        console.log("error", error);
        errorList.push("에러 데이터 파싱 실패 : eCT215");
      }
    }
    // s2에서 공용공간이 모두 비활성화인 경우 개별 핸들링
    if (response?.data?.meta?.errorCode === "eCT218") {
      errorList.push("공용공간 비활성화");
    }
    // s2에서 공용공간이 활성화지만 공용공간을 미등록한 경우 개별 핸들링
    if (response?.data?.meta?.errorCode === "eCT219") {
      errorList.push("공용공간 미등록");
    }

    console.log("errorList", errorList);

    setBaseModal({
      isOpen: true,
      title: `계약 생성 가능으로 변경할 수 없습니다.`,
      children: (
        <div className="flex-d flex-row">
          <p>아래의 필수 정보가 입력되지 않았습니다.</p>
          <p>확인 후 다시 시도해주세요</p>
          <div className="w-100 text-left py10 px15 mt20 overflow-scroll" style={{ backgroundColor: "#F2F4F6", maxHeight: 250 }}>
            {errorList.length > 0 &&
              errorList.map((error: string) => {
                return (
                  <div key={error}>
                    • <span>{error}</span>
                  </div>
                );
              })}
          </div>
        </div>
      ),
      btnRightTitle: "확인",
    });
  };

  const handleChangeContractAvail = async () => {
    // 계약 가능 여부 변경 가능 여부 체크
    if (isContractAvail === true) {
      // s2 타임코트 상품일 경우 앞에서 유효성 체크 후 얼리리턴 모달 띄우기
      if (product?.productType === ProductTypes.TIME_COURT) {
        // 프로바이더 정산정보 유효성 체크
        if (product?.isOnlinePayment) {
          if (providerPopbillApi?.isVerifiedCashbill !== true || providerPopbillApi?.isVerifiedTaxInvoice !== true) {
            //둘중 하나라도 유효하지 않으면 모달 띄우기 종료
            return setBaseModal({
              isOpen: true,
              children: "해당 상품 프로바이더의 세금계산서 발행 및 현금영수증 발급 설정이 비활성화되어 있습니다. 관리자에게 문의하세요.",
              title: "설정에 실패했습니다",
              btnRightTitle: "확인",
            });
          }
          // 프로바이더 정산정보 유효성 체크
          if (providerNormalCardApi?.isVerifiedPaymentKey !== true) {
            return setBaseModal({
              isOpen: true,
              children: "‘정산정보 > 결제 연동 정보'에서 해당 프로바이더의 '신용카드 일반결제' 정보를 등록해 주세요.",
              title: "설정에 실패했습니다.",
              btnRightTitle: "확인",
            });
          }
        }
      }

      // 가능으로 변경하려는 경우에만 체크
      try {
        await getProductCheckRca94({ productId: String(product?.id) });
      } catch (error) {
        const errorResponse = error instanceof AxiosError ? error.response : undefined;
        if (!errorResponse) {
          return openErrorModal({ cause: error });
        }
        // 계약 가능 여부 변경 가능 여부 체크 실패시 커스텀 에러 모달 띄우고 종료
        return openCustomErrorModel(errorResponse);
      }
    }

    // ------ 아래부터 계약 가능 여부 변경 요청 false | true 경우 공통 로직 -----

    // product 의 isContractAvail 를 변경하는 api 호출
    try {
      await changeProductAvailable({ product: { id: Number(product?.id), isContractAvail: isContractAvail } });
    } catch (error) {
      const errorResponse = error instanceof AxiosError ? error.response : undefined;
      if (!errorResponse) {
        return openErrorModal({ cause: error });
      }
      // 계약 가능 여부 변경 요청 실패시 커스텀 에러 모달 띄우고 종료
      return openCustomErrorModel(errorResponse);
    }

    // ------ 아래부터 계약 가능 여부 변경 요청 false 경우 로직 -----

    // isContractAvail가 false 일경우 상품 상태도 DISABLED로 변경해줘야함
    if (isContractAvail === false) {
      // product 의 status 를 DISABLED 로 변경하는 api 호출
      await updateProduct({
        id: Number(product?.id),
        status: "DISABLED",
      });

      //isContractAvail 체크 및 status 상태 변경 확인
      const getProductRefetchData = await refetch();

      console.log("getProductRefetchData?.data?.isContractAvail", getProductRefetchData?.data?.isContractAvail);
      console.log("getProductRefetchData?.data?.status", getProductRefetchData?.data?.status);

      if (getProductRefetchData?.data?.isContractAvail !== false || getProductRefetchData?.data?.status !== "DISABLED") {
        return setBaseModal({
          isOpen: true,
          children:
            "계약 가능 여부는 '불가능'으로 변경되었으나, 상품 공개 상태가 '비공개'로 전환되지 않았습니다. 상품 수정 화면에서 '비공개'로 변경해 주세요.",
          title: "공개 상태가 정상적으로 변경되지 않았습니다.",
          btnRightTitle: "확인",
        });
      }
    }

    // 전부 통과하면 상품 refetch 하고 모달 닫기
    refetch();
    setBaseModal({ isOpen: false });
  };

  return (
    <>
      <div className="flex-center-start base-modal__title keep-all pre-formatted">
        <p className="mb30 font24 text-primary">
          <b>계약 가능 여부</b>
        </p>
      </div>

      <div className="flex-center-start">
        <p className="required font14 mr10">상태변경</p>

        <BaseRadio
          className="mr10"
          id="false-isContractAvail"
          name="isContractAvail"
          label="불가능"
          onChange={() => setIsContractAvail(false)}
          checked={isContractAvail === false}
        />
        <BaseRadio
          className="mr10"
          id="true-isContractAvail"
          name="isContractAvail"
          label="가능"
          onChange={() => setIsContractAvail(true)}
          checked={isContractAvail === true}
        />
      </div>
      <div className="base-modal__btn-wrap flex-center-end">
        <BaseButton
          title={"취소"}
          onClick={() => {
            setBaseModal({ isOpen: false });
          }}
          className="color-white mr10"
        />

        <BaseButton
          title={"저장"}
          onClick={async () => {
            await handleChangeContractAvail();
          }}
        />
      </div>
    </>
  );
};

export default ContractAvailModal;
